import {
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    format,
    parse,
    addMinutes,
    isBefore,
    isAfter,
    getYear,
    getMonth,
  } from 'date-fns';
  import { fetchDataForMonth } from './firebaseQueries'; // deine Funktion, die den Monat in 1 Query lädt
  import { enUS } from 'date-fns/locale'; // nur wenn du englische Wochentage willst
  
  /**
   * Hauptfunktion, die du z.B. in `BookingPage` aufrufst:
   * fetchAvailableDaysForMonth(tenantId, monthDate, selectedAppointmentType, selectedEmployee, appointmentTypes, employees)
   */
  export async function fetchAvailableDaysForMonth(
    tenantId,
    monthDate,
    appointmentTypeId,
    employeeId,
    appointmentTypes,
    employees,
    // Falls du einen Cache nutzen willst
    monthCache,
    setMonthCache
  ) {
    const year = getYear(monthDate);
    const month = getMonth(monthDate);
    const firstDay = startOfMonth(new Date(year, month, 1));
    const lastDay = endOfMonth(firstDay);
  
    // z.B. "2024-01" + employeeId als Cache-Key
    const cacheKey = `${format(firstDay, 'yyyy-MM')}-${employeeId}`;
    if (monthCache && monthCache[cacheKey]) {
      console.log('Cache-TREFFER: Verwende bereits geladene Monatsdaten für', cacheKey);
      const { monthAvailabilities, monthAbsences, monthAppointments } = monthCache[cacheKey];
      return computeAvailableDays(
        monthAvailabilities,
        monthAbsences,
        monthAppointments,
        firstDay,
        lastDay,
        appointmentTypeId,
        employeeId,
        appointmentTypes
      );
    }
  
    console.log('Lade Monatsdaten aus Firestore für', format(firstDay, 'yyyy-MM'), '...');
  
    // 1) ALLE Verfügbarkeiten / Absences / Appointments dieses Monats in EINER Abfrage
    const { monthAvailabilities, monthAbsences, monthAppointments } = await fetchDataForMonth(
      tenantId,
      firstDay,
      lastDay,
      employeeId
    );
  
    console.log('monthAvailabilities:', monthAvailabilities);
    console.log('monthAbsences:', monthAbsences);
    console.log('monthAppointments:', monthAppointments);
  
    // Speichere ggf. in Cache
    if (setMonthCache) {
      setMonthCache((prev) => ({
        ...prev,
        [cacheKey]: { monthAvailabilities, monthAbsences, monthAppointments },
      }));
    }
  
    // Nun lokales Durchiterieren
    return computeAvailableDays(
      monthAvailabilities,
      monthAbsences,
      monthAppointments,
      firstDay,
      lastDay,
      appointmentTypeId,
      employeeId,
      appointmentTypes
    );
  }
  
  /**
   * computeAvailableDays:
   * Geht Tag für Tag durch und prüft, ob mind. ein Slot frei ist.
   * Hier bauen wir überall LOGS ein, um zu sehen, warum kein Tag frei ist.
   */
  function computeAvailableDays(
    monthAvailabilities,
    monthAbsences,
    monthAppointments,
    firstDay,
    lastDay,
    appointmentTypeId,
    employeeId,
    appointmentTypes
  ) {
    console.log('==== computeAvailableDays START ====');
  
    // Alle Tage im Monat
    const allDays = eachDayOfInterval({ start: firstDay, end: lastDay });
    // Gesuchte Terminart (für Dauer, assignedEmployees etc.)
    const selectedType = appointmentTypes.find((t) => t.id === appointmentTypeId);
  
    console.log('selectedType:', selectedType);
    console.log('employeeId:', employeeId);
  
    const availableDays = [];
  
    // Schleife über alle Tage des Monats
    for (const day of allDays) {
      const dateStr = format(day, 'yyyy-MM-dd');
      // Da du englische Wochentage in DB hast, holen wir den Wochentag explizit in Englisch:
      const dayOfWeek = format(day, 'EEEE', { locale: enUS });
      console.log(`\n>> Prüfe Tag: ${dateStr} (Weekday: ${dayOfWeek}) <<`);
  
      let dayHasFreeSlot = false;
  
      // --- 1) Availabilities filtern (z.B. Wochentage, employee, etc.)
      const dailyAvailabilities = monthAvailabilities.filter((a) => {
        // Filter Mitarbeiter
        if (employeeId && a.employeeId !== employeeId) return false;
        // Filter Terminart => a.employeeId muss in assignedEmployees sein
        if (selectedType && !selectedType.assignedEmployees.includes(a.employeeId)) return false;
        // Wochentag => wenn a.weekdays NICHT dayOfWeek enthält, skip
        if (!a.weekdays.includes(dayOfWeek)) return false;
        return true;
      });
  
      console.log(`   dailyAvailabilities (nach Filter):`, dailyAvailabilities);
  
      // --- 2) Absences filtern
      //  Hier muss in Firestore "date" = dateStr sein, oder du hast fromDate/toDate, etc.
      const dailyAbsences = monthAbsences.filter((abs) => {
        return abs.employeeId === employeeId && abs.date === dateStr; 
        // Falls du fromDate/toDate hast, musst du anders filtern.
      });
      console.log(`   dailyAbsences:`, dailyAbsences);
  
      // --- 3) Appointments filtern
      const dailyAppointments = monthAppointments.filter((app) => {
        return app.employeeId === employeeId && app.date === dateStr && app.canceled === false;
      });
      console.log(`   dailyAppointments:`, dailyAppointments);
  
      // Falls Abwesenheit existiert -> der Tag ist ggf. komplett blockiert
      // Du kannst, je nach Logik, hier dayHasFreeSlot = false lassen und skippen
      // oder du machst es pro availability-Loop.
  
      // --- 4) Slot-Berechnung
      for (const availability of dailyAvailabilities) {
        // Wenn es Absences gibt, direkt skip
        if (dailyAbsences.some((ab) => ab.employeeId === availability.employeeId)) {
          console.log(`   -> Abwesenheit blockiert Mitarbeiter ${availability.employeeId} an Tag ${dateStr}`);
          continue;
        }
  
        // Dauer
        const duration = selectedType?.duration || 60;
  
        // Start/End
        const availabilityStart = parse(availability.startTime, 'HH:mm', day);
        const availabilityEnd = parse(availability.endTime, 'HH:mm', day);
  
        console.log(`   -> Availability Start/End: ${format(availabilityStart, 'HH:mm')} - ${format(availabilityEnd, 'HH:mm')}`);
  
        let freeSegments = [{ start: availabilityStart, end: availabilityEnd }];
  
        // Vorhandene Termine subtrahieren
        const empAppointments = dailyAppointments.filter((ap) => ap.employeeId === availability.employeeId);
        for (const appt of empAppointments) {
          const apptStart = parse(appt.startTime, 'HH:mm', day);
          const apptEnd = addMinutes(apptStart, appt.duration);
  
          console.log(`     -> Termin blockiert: ${format(apptStart, 'HH:mm')} - ${format(apptEnd, 'HH:mm')}`);
  
          freeSegments = freeSegments.flatMap((seg) => {
            if (isBefore(seg.end, apptStart) || isAfter(seg.start, apptEnd)) {
              // segment berührt Termin nicht
              return [seg];
            }
            // Segment teilen
            const newSegs = [];
            if (isBefore(seg.start, apptStart)) {
              newSegs.push({ start: seg.start, end: apptStart });
            }
            if (isAfter(seg.end, apptEnd)) {
              newSegs.push({ start: apptEnd, end: seg.end });
            }
            return newSegs;
          });
        }
  
        // Checken, ob mind. 1 Slot >= duration existiert
        for (const seg of freeSegments) {
          let slotStart = seg.start;
          while (addMinutes(slotStart, duration) <= seg.end) {
            console.log(
              `     -> freier Slot: ${format(slotStart, 'HH:mm')} - ${format(addMinutes(slotStart, duration), 'HH:mm')}`
            );
            // Wir haben mind. einen freien Slot => Tag gilt als buchbar
            dayHasFreeSlot = true;
            break;
          }
          if (dayHasFreeSlot) {
            console.log('   -> Tag hat mind. einen freien Slot. Breche ab.');
            break;
          }
        }
  
        if (dayHasFreeSlot) break;
      }
  
      if (dayHasFreeSlot) {
        availableDays.push(dateStr);
        console.log(`>> Tag ${dateStr} wird in availableDays gepusht.`);
      } else {
        console.log(`>> Tag ${dateStr} hat KEINE freien Slots.`);
      }
    } // Ende Schleife allDays
  
    console.log('==== computeAvailableDays ENDE ====');
    console.log('Ergebnis: availableDays =', availableDays);
    return availableDays;
  }
  